// Splits a wide camera frame into two squares for the person segmenter and
// joins their masks back into one.
//
// The segmenter takes a square. Handed a 16:9 frame whole, it squeezes the
// frame to fit, and a raised arm or hand comes out thin and half-sure: Adam's
// raised forearm read 0.3 to 0.6, under the edge cutoff, so it was blurred
// (2026-10-14: "it doesn't recognize hands or arms at all"). The same model on
// an unsqueezed square of the same frame reads the hand and forearm near 1 and
// still leaves his chair out. Padding the frame to a square instead let the
// chair and the padding itself in, so the frame is cut into a left and a right
// square that overlap in the middle, where the person usually sits.
//
// Across the overlap each square counts for more the farther it is from its
// own edge, since a model sees least well at the edge of what it was shown.

export interface TileRect {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/** Below this width-to-height ratio the frame is sent whole. */
const WIDE_ABOVE = 1.25;

/** The parts of a w by h frame to segment: two squares across a wide frame,
 * otherwise the whole frame. A tall frame is sent whole too: its lower square
 * would hold a torso with no face, which the selfie model reads poorly. */
export function planTiles(w: number, h: number): TileRect[] {
  if (w <= 0 || h <= 0 || w / h <= WIDE_ABOVE) return [{ x: 0, y: 0, w, h }];
  return [
    { x: 0, y: 0, w: h, h },
    { x: w - h, y: 0, w: h, h },
  ];
}

export interface TileMask {
  readonly data: Float32Array;
  readonly width: number;
  readonly height: number;
}

export interface TileJoin {
  /** The joined mask's size. Its aspect is the frame's. */
  readonly width: number;
  readonly height: number;
  /** One mask per tile, in planTiles order, each the same size as when the
   * join was made. */
  join(masks: readonly TileMask[]): Float32Array;
}

/** Builds the lookup that joins tile masks of maskW by maskH into one mask
 * for a frameW by frameH frame, sized so its short side is the masks' short
 * side. Build once per frame size; joining is then a weighted read per pixel. */
export function tileJoiner(frameW: number, frameH: number, tiles: readonly TileRect[], maskW: number, maskH: number): TileJoin {
  const short = Math.min(maskW, maskH);
  const scale = short / Math.min(frameW, frameH);
  const width = Math.max(1, Math.round(frameW * scale));
  const height = Math.max(1, Math.round(frameH * scale));
  const n = width * height;
  // Up to two tiles cover any pixel: their mask indices and the first's weight.
  const idxA = new Int32Array(n);
  const idxB = new Int32Array(n).fill(-1);
  const weightA = new Float32Array(n).fill(1);
  for (let oy = 0; oy < height; oy++) {
    const fy = ((oy + 0.5) / height) * frameH;
    for (let ox = 0; ox < width; ox++) {
      const fx = ((ox + 0.5) / width) * frameW;
      const i = oy * width + ox;
      let found = 0;
      let reachA = 0;
      for (let t = 0; t < tiles.length && found < 2; t++) {
        const r = tiles[t]!;
        if (fx < r.x || fx >= r.x + r.w || fy < r.y || fy >= r.y + r.h) continue;
        const mx = Math.min(maskW - 1, Math.floor(((fx - r.x) / r.w) * maskW));
        const my = Math.min(maskH - 1, Math.floor(((fy - r.y) / r.h) * maskH));
        const at = t * maskW * maskH + my * maskW + mx;
        // How far in from this tile's nearer side edge the pixel sits.
        const reach = Math.max(1e-3, Math.min(fx - r.x, r.x + r.w - fx));
        if (found === 0) {
          idxA[i] = at;
          reachA = reach;
        } else {
          idxB[i] = at;
          weightA[i] = reachA / (reachA + reach);
        }
        found++;
      }
    }
  }
  const tileSize = maskW * maskH;
  const all = new Float32Array(tileSize * tiles.length);
  const out = new Float32Array(n);
  return {
    width,
    height,
    join(masks) {
      for (let t = 0; t < tiles.length; t++) {
        const m = masks[t];
        if (!m || m.width !== maskW || m.height !== maskH) throw new Error("tile mask size changed");
        all.set(m.data, t * tileSize);
      }
      for (let i = 0; i < n; i++) {
        const b = idxB[i]!;
        const a = all[idxA[i]!]!;
        out[i] = b < 0 ? a : a * weightA[i]! + all[b]! * (1 - weightA[i]!);
      }
      return out;
    },
  };
}
